import { manufacturingFetch } from "@/lib/manufacturingClient";
import type { MfgRecipeKind } from "@/lib/manufacturing/types";

export type BomMatchBand = "high" | "ambiguous" | "none";

export type BomMatchCandidate = {
  id: string;
  kind: "material" | "component_recipe" | "material_use" | "named_variant";
  code: string;
  name: string;
  score: "exact" | "fuzzy";
};

export type BomAuthoringMember = {
  intent_name: string;
  quantity: number;
  role: string;
  slot_label: string;
  upgrade_price_delta: number | null;
  band: BomMatchBand;
  candidates: BomMatchCandidate[];
  auto_attach: BomMatchCandidate | null;
};

export type BomAuthoringStackFrame = {
  recipe_id: string | null;
  pending_line_id: string | null;
  label: string;
};

export type BomAuthoringDraft = {
  code: string;
  name: string;
  kind: MfgRecipeKind;
  tags: string[];
  members: BomAuthoringMember[];
  stack: BomAuthoringStackFrame[];
};

export type BomAuthoringSessionSummary = {
  id: string;
  status: "open" | "parked" | "completed" | "cancelled";
  root_recipe_id: string | null;
  recipe_group_id: string | null;
  opened_by_user_id: string | null;
  updated_by_user_id: string | null;
  source: "typed" | "spoken" | "mixed";
  brief_text: string;
  draft: BomAuthoringDraft;
  created_at: string;
  updated_at: string;
};

export function listManufacturingBomAuthoringSessions(opts?: {
  handoff_only?: boolean;
}) {
  const q = new URLSearchParams();
  if (opts?.handoff_only) q.set("handoff_only", "1");
  const suffix = q.toString() ? `?${q.toString()}` : "";
  return manufacturingFetch<{ ok: true; sessions: BomAuthoringSessionSummary[] }>(
    `/api/mfg/bom-authoring-sessions${suffix}`,
    { method: "GET" }
  );
}

export function getManufacturingBomAuthoringSession(sessionId: string) {
  const q = new URLSearchParams({ session_id: sessionId });
  return manufacturingFetch<{ ok: true; session: BomAuthoringSessionSummary }>(
    `/api/mfg/bom-authoring-sessions/detail?${q.toString()}`,
    { method: "GET" }
  );
}

export function createManufacturingBomAuthoringSession(payload: {
  brief_text: string;
  source?: "typed" | "spoken" | "mixed";
  recipe_group_id?: string | null;
  code?: string;
  name?: string;
  kind?: MfgRecipeKind;
  tags?: string[];
  members?: Array<{
    intent_name: string;
    quantity?: number;
    role?: string;
    slot_label?: string;
    upgrade_price_delta?: number | null;
  }>;
}) {
  return manufacturingFetch<{ ok: true; session: BomAuthoringSessionSummary }>(
    "/api/mfg/bom-authoring-sessions/create",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function parkManufacturingBomAuthoringSession(payload: {
  session_id: string;
  draft?: BomAuthoringDraft;
}) {
  return manufacturingFetch<{ ok: true; session: BomAuthoringSessionSummary }>(
    "/api/mfg/bom-authoring-sessions/park",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function resumeManufacturingBomAuthoringSession(sessionId: string) {
  return manufacturingFetch<{ ok: true; session: BomAuthoringSessionSummary }>(
    "/api/mfg/bom-authoring-sessions/resume",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId }),
    }
  );
}

export function completeManufacturingBomAuthoringSession(sessionId: string) {
  return manufacturingFetch<{ ok: true; session: BomAuthoringSessionSummary }>(
    "/api/mfg/bom-authoring-sessions/complete",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId }),
    }
  );
}

export function cancelManufacturingBomAuthoringSession(sessionId: string) {
  return manufacturingFetch<{ ok: true; session: BomAuthoringSessionSummary }>(
    "/api/mfg/bom-authoring-sessions/cancel",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId }),
    }
  );
}

export function commitManufacturingBomAuthoringSessionRoot(sessionId: string) {
  return manufacturingFetch<{
    ok: true;
    session: BomAuthoringSessionSummary;
    recipe_id: string;
  }>("/api/mfg/bom-authoring-sessions/commit-root", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId }),
  });
}

export function spawnManufacturingBomAuthoringChild(payload: {
  session_id: string;
  pending_line_id: string;
  code?: string;
  name?: string;
  kind?: MfgRecipeKind;
}) {
  return manufacturingFetch<{
    ok: true;
    session: BomAuthoringSessionSummary;
    child_recipe_id: string;
  }>("/api/mfg/bom-authoring-sessions/spawn-child", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function promoteManufacturingBomAuthoringChild(sessionId: string) {
  return manufacturingFetch<{ ok: true; session: BomAuthoringSessionSummary }>(
    "/api/mfg/bom-authoring-sessions/promote-child",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId }),
    }
  );
}

export function updateManufacturingBomAuthoringSessionDraft(payload: {
  session_id: string;
  draft: BomAuthoringDraft;
}) {
  return manufacturingFetch<{ ok: true; session: BomAuthoringSessionSummary }>(
    "/api/mfg/bom-authoring-sessions/update-draft",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}
